import type { AddressShipmentOptional } from "../types";
import type {
  ContainerOrderRoute,
  ContainerStopKind,
  ContainerStopRole,
  OrderAdditionalStop,
  StopUsage,
} from "../types/containerOrders";

/**
 * The route an order actually describes, in the order it is driven.
 *
 * An order does not hold its route as a list. It holds a *template*: three
 * named anchors — collect the empty box, load it, return it — plus a list of
 * added stops, each anchored into one of the gaps between them by `after` and
 * ordered within that gap by `sequence`. Two things then make the stored shape
 * and the driven route different documents:
 *
 *  - stops can be added anywhere and reordered within their gap, so the route
 *    is not three stops and can run to {@link MAX_ROUTE_STOPS};
 *  - either terminal can be switched off — the box may already be standing at
 *    the client's site, or be staying there — so an anchor being present in the
 *    data does not mean a truck goes to it.
 *
 * Flattening the one into the other is what `buildStops` does on the server
 * when a container is generated. This is the read-only twin of that function,
 * for anywhere the route has to be *shown* rather than executed, and the two
 * must stay in step: if the anchoring rule changes there, it changes here.
 * (`containerOrderService.buildStops` is the original — this deliberately
 * mirrors its sequence, gap by gap.)
 */

/** One stop on the derived route, reduced to what a reader needs. */
export interface RouteStopSummary {
  /** Stable within one route — index-based, since stops have no id here. */
  key: string;
  /**
   * What to call this stop: its label, its company, or its town.
   *
   * Can be empty for an added stop that was never named — its `kind` is then
   * the name ("Weighbridge"), which only the caller can translate.
   */
  label: string;
  role: ContainerStopRole;
  /** What an added stop is for. Absent on the three anchors. */
  kind?: ContainerStopKind;
}

/**
 * The part of a stop this module reads — the three stop shapes have little else
 * in common, and nothing here needs the rest of them.
 */
type NameableStop = StopUsage & {
  companyName?: string;
  label?: string;
  address?: AddressShipmentOptional;
};

/** A stop counts unless it was explicitly switched off. */
const isEnabled = (stop?: StopUsage) => stop?.enabled !== false;

/**
 * What to call a stop.
 *
 * The company first, because that is the place: a stop's `label` defaults from
 * its kind, so leading with it turns "MUUGA PORT CUSTOMS" into a bare
 * "Customs" and loses which customs. The label is next — it is what the
 * dispatcher named the stop and what the driver is shown — and then the town,
 * the last thing that still says where the truck is going.
 */
const nameOf = (stop?: NameableStop): string =>
  (stop?.companyName || stop?.label || stop?.address?.city || "").trim();

/**
 * The enabled stops of a route, in driving order.
 *
 * Nameless stops are dropped — not because they are not part of the route, but
 * because a blank in a chain of names reads as a rendering fault rather than as
 * an anchor nobody filled in. This is a display helper; execution keeps them.
 */
export const orderedRouteStops = (
  route?: ContainerOrderRoute,
): RouteStopSummary[] => {
  if (!route) return [];

  // Added stops, grouped by the gap they were anchored into and ordered within
  // it — the same grouping the server does before it interleaves them.
  const added = new Map<string, OrderAdditionalStop[]>();
  for (const stop of route.additionalStops ?? []) {
    const gap = stop.after || "start";
    if (!added.has(gap)) added.set(gap, []);
    added.get(gap)!.push(stop);
  }
  for (const list of added.values()) {
    list.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  }

  const stops: RouteStopSummary[] = [];
  let index = 0;

  const push = (
    role: ContainerStopRole,
    stop: NameableStop,
    opts: { skippable?: boolean; kind?: ContainerStopKind } = {},
  ) => {
    // The loading site is the work the order exists for and can never be
    // switched off; the server hard-codes it enabled for the same reason.
    const { skippable = true, kind } = opts;
    if (skippable && !isEnabled(stop)) return;

    const label = nameOf(stop);
    // An anchor with no name was never filled in, and a blank in a chain of
    // names reads as a rendering fault rather than as a gap in the data. An
    // added stop always has a kind to be called by, so it keeps its place.
    if (!label && !kind) return;

    stops.push({ key: `${role}-${index++}`, label, role, kind });
  };

  const pushGap = (gap: string) => {
    for (const stop of added.get(gap) ?? []) {
      push("custom", stop, { kind: stop.kind || "other" });
    }
  };

  pushGap("start");
  push("terminalPickup", route.terminalPickup ?? {});
  pushGap("terminalPickup");
  push("loadingSite", route.loadingSite ?? {}, { skippable: false });
  pushGap("loadingSite");
  push("terminalDropoff", route.terminalDropoff ?? {});
  pushGap("terminalDropoff");

  return stops;
};

/**
 * A container's own run, as the same summaries.
 *
 * The container side needs no flattening: a line stores `routeStops` already
 * flat and already ordered, because `buildStops` did that work when the line
 * was generated. What it does need is the same *shape*, so one component can
 * draw an order's route and a container's without knowing which it has.
 *
 * Read off the line and never off its order, which is the point of the column:
 * a container inherits the order's route and may be sent elsewhere afterwards,
 * and the table is the one place a diverged box shows without being opened.
 */
export const lineRouteStops = (
  stops: {
    _id?: string;
    role: ContainerStopRole;
    kind?: ContainerStopKind;
    label?: string;
    companyName?: string;
    address?: AddressShipmentOptional;
  }[],
): RouteStopSummary[] =>
  stops.map((stop, index) => ({
    key: stop._id || `${stop.role}-${index}`,
    label: nameOf(stop),
    role: stop.role,
    kind: stop.kind,
  }));
